import React, { useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import localeText from "../../../../../locale";
import { usePayment } from "../../Checkout/context/PaymentContext";
import { useExtracreditPayment } from "../../Checkout/hooks/useExtracreditPayment";
import { formatPrice } from "../../../../../utilities/formatPrice";

const SinglePaymentExtracredit = ({
  currencyProduct,
  currencyFormat,
  extracoinInWallet,
  combinePayment,
  totalPrice,
  isInCombinedAccordion = false,
}) => {
  const {
    selectedPaymentMethod,
    setAmountExtracredit,
    isPaymentMethodAvailable,
    isTotalCovered,
    isPaymentMethodInUse,
    loading,
  } = usePayment();
  const { amountToPayWithExtracredit, maxAmount, handleAmountChange, clearExtracreditPayment } =
    useExtracreditPayment();

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod !== "extracredit") {
      if (amountToPayWithExtracredit !== 0) {
        clearExtracreditPayment();
      }
    }
  }, [selectedPaymentMethod, clearExtracreditPayment, combinePayment, amountToPayWithExtracredit]);

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod === "extracredit" && !isInCombinedAccordion) {
      setAmountExtracredit(totalPrice);
    }
  }, [
    selectedPaymentMethod,
    combinePayment,
    totalPrice,
    isInCombinedAccordion,
    setAmountExtracredit,
  ]);

  const hasExtracredit = extracoinInWallet > 0;
  const hasEnoughExtracredit = extracoinInWallet >= totalPrice;
  const isAvailable = isPaymentMethodAvailable("extracredit");
  const totalCovered = isTotalCovered();
  const isInUse = isPaymentMethodInUse("extracredit");

  if (combinePayment && totalCovered && !isInUse && !loading) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-blue-50 border-round border-blue-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle text-blue-600"></i>
            <span className="text-blue-800 font-medium">{localeText("totalAlreadyCovered")}</span>
          </div>
          <small className="text-blue-700 mt-1 block">
            {localeText("paymentCompleteWithOtherMethodsExplanation")}
          </small>
        </div>
      </div>
    );
  }

  if (combinePayment && !isAvailable && !loading) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-gray-50 border-round border-gray-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-ban text-gray-600"></i>
            <span className="text-gray-800 font-medium">{localeText("methodNotAvailable")}</span>
          </div>
          <small className="text-gray-700 mt-1 block">
            {localeText("methodNotAvailableExplanation")}
          </small>
        </div>
      </div>
    );
  }

  if (!hasExtracredit) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-yellow-50 border-round border-yellow-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-yellow-600"></i>
            <span className="text-yellow-800 font-medium">
              {localeText("noExtracreditAvailable")}
            </span>
          </div>
          <small className="text-yellow-700 mt-1 block">{localeText("extracreditRequired")}</small>
        </div>
      </div>
    );
  }

  if (!combinePayment && !hasEnoughExtracredit) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-orange-50 border-round border-orange-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-orange-600"></i>
            <span className="text-orange-800 font-medium">
              {localeText("insufficientExtracredit")}
            </span>
          </div>
          <small className="text-orange-700 mt-1 block">
            {localeText("insufficientExtracreditMessage")
              .replace("{currency}", currencyFormat[currencyProduct])
              .replace("{amount}", formatPrice(extracoinInWallet))
              .replace("{total}", formatPrice(parseFloat((Number(totalPrice) || 0).toFixed(2))))}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-column">
      <label htmlFor="extracredit-amount" className="font-medium">
        {localeText("amountToPay")}
      </label>
      <InputNumber
        id="extracredit-amount"
        value={combinePayment || isInCombinedAccordion ? amountToPayWithExtracredit : totalPrice}
        onValueChange={(e) => handleAmountChange(e.value, combinePayment, totalPrice)}
        mode="currency"
        currency={currencyProduct}
        locale="en-US"
        min={0}
        max={combinePayment || isInCombinedAccordion ? maxAmount : totalPrice}
        className="w-full mb-1"
        disabled={(!combinePayment && !isInCombinedAccordion) || (combinePayment && !isAvailable)}
      />
      <small className="text-color-secondary">{localeText("extracreditAmountDescription")}</small>
    </div>
  );
};

export default SinglePaymentExtracredit;
